import { COMPANY, configuredCompanyDetails, configuredContacts, Page, Rule, ui } from '../ui';
import { useSiteConfig } from '../site-config';
import { useDocumentTitle } from '../title';

/**
 * Contact.
 *
 * Renders only channels that actually exist. Section 29.2: a contact page
 * listing an address nobody monitors is worse than one saying the channel is
 * not open yet, because the first quietly loses the message and the second
 * sends the person somewhere that works.
 *
 * **The trader block is the reason this page could not simply become a static
 * component.** A storefront selling to EU consumers has to identify its trader,
 * and the sentence that does it is derived from whichever payment provider the
 * *deployment* uses — not from whatever was true on the machine that built the
 * bundle. So it comes from `GET /v1/site`, and while the request is out this
 * section says so rather than showing a placeholder trader.
 *
 * The Discord row comes from the same response, so it appears here the moment
 * the invite is set on the API rather than at the next front-end release.
 */
export function ContactRoute(): React.ReactElement {
  useDocumentTitle('Contact');

  const site = useSiteConfig();

  const contacts = configuredContacts();
  const companyDetails = configuredCompanyDetails();
  const invite = site.data?.discordInvite ?? null;
  const nothingConfigured = contacts.length === 0 && invite === null;

  return (
    <Page site="store">
      <header className={ui.prose ?? ''}>
        <p className="gb-eyebrow">Contact</p>
        <h1 className="gb-headline">Talk to us</h1>
        {/*
          The operator sentence used to open this page and now closes it, under
          `The trader` where the rest of the company's identifiers already are.
          It is a legal statement rather than a greeting, and running it as the
          lead meant that the first thing anybody read on the page they opened
          to ask a question was our company's registered name.
        */}
        <p className={`gb-body ${ui.lede ?? ''}`}>A person reads everything sent here.</p>
      </header>

      <Rule />

      {contacts.length > 0 ? (
        <section className={ui.prose ?? ''}>
          <h2 className="gb-subhead">By email</h2>
          <ul style={ROWS}>
            {contacts.map((contact) => (
              <li key={contact.address} style={ROW}>
                <span className="gb-data-label" style={LABEL}>
                  {contact.role}
                </span>
                <a className="gb-body" href={`mailto:${contact.address}`}>
                  {contact.address}
                </a>
              </li>
            ))}
          </ul>
        </section>
      ) : null}

      {invite ? (
        <section className={ui.prose ?? ''}>
          <h2 className="gb-subhead">Chat</h2>
          <ul style={INLINE}>
            <li>
              <a className="gb-body" href={invite} rel="me noopener noreferrer" target="_blank">
                Discord
              </a>
            </li>
          </ul>
        </section>
      ) : null}

      {/*
        Section 8: an empty state is an invitation, never an apology — but it
        must also not pretend a channel exists.
      */}
      {nothingConfigured ? (
        <section className={ui.prose ?? ''}>
          <p className={`gb-body ${ui.pending ?? ''}`}>
            No way to reach us yet. One will be here as soon as it opens.
          </p>
        </section>
      ) : null}

      <Rule />

      {/*
        Who the buyer is actually dealing with. The identifiers below render
        only once they are confirmed — a plausible-looking company number is
        worse than an absent one, because it is exactly the field somebody would
        use to check.
      */}
      <section className={ui.prose ?? ''}>
        <h2 className="gb-subhead">Who you buy from</h2>
        <p className="gb-body">{COMPANY.operatorLine}</p>
        {site.data ? (
          <p className="gb-body">{site.data.traderLine}</p>
        ) : (
          <p className={`gb-body ${ui.pending ?? ''}`}>
            {site.error
              ? 'We could not load who bills you. Try again, or write to us and we will tell you.'
              : 'Loading who bills you…'}
          </p>
        )}
        {companyDetails.length > 0 ? (
          <ul style={ROWS}>
            {companyDetails.map((detail) => (
              <li key={detail.label} style={ROW}>
                <span className="gb-data-label" style={LABEL}>
                  {detail.label}
                </span>
                <span className="gb-body">{detail.value}</span>
              </li>
            ))}
          </ul>
        ) : null}
      </section>

      <Rule />

      <section className={ui.prose ?? ''}>
        <h2 className="gb-subhead">Security</h2>
        <p className="gb-body">
          Found a security hole? Tell us privately and give us time to fix it. We will never go
          after somebody who reports one in good faith.
        </p>
      </section>
    </Page>
  );
}

const ROWS: React.CSSProperties = { listStyle: 'none', margin: 0, padding: 0 };

const ROW: React.CSSProperties = {
  display: 'flex',
  gap: 'var(--gb-space-4)',
  padding: 'var(--gb-space-3) 0',
  borderBottom: 'var(--gb-rule-width) solid var(--gb-rule)',
};

const LABEL: React.CSSProperties = { color: 'var(--gb-text-faint)', minWidth: '10rem' };

const INLINE: React.CSSProperties = {
  listStyle: 'none',
  margin: 0,
  padding: 0,
  display: 'flex',
  gap: 'var(--gb-space-6)',
  flexWrap: 'wrap',
};
